import { useMutation } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useBulkExportTimeEntries() {
  return useMutation({
    mutationFn: async (entryIds: string[]) => {
      const supabase = getSupabase();

      const { data, error } = await supabase
        .from("time_entries")
        .select(
          `
          *,
          user:user_id (
            first_name,
            last_name,
            email
          ),
          ticket:ticket_id (
            title,
            client_id,
            contacts:client_id (
              name,
              company_name
            )
          )
        `
        )
        .in("id", entryIds);

      if (error) {
        console.error("Error fetching time entries for export:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No time entries found to export");
      }

      const {
        objectsToCSV,
        downloadCSV,
        formatDateForCSV,
        formatNumberForCSV,
        formatBooleanForCSV,
      } = await import("@/lib/utils/csv-export");

      const exportData = data.map((entry) => ({
        ID: entry.id,
        Description: entry.description || "",
        Hours: formatNumberForCSV(entry.hours),
        "Entry Type": entry.entry_type || "",
        "Billable Rate": formatNumberForCSV(entry.billable_rate),
        "Date Worked": formatDateForCSV(entry.date_worked),
        "Start Time": entry.start_time || "",
        "End Time": entry.end_time || "",
        "Is Approved": formatBooleanForCSV(entry.is_approved),
        "Approved At": formatDateForCSV(entry.approved_at),
        "User Name": entry.user
          ? `${entry.user.first_name || ""} ${entry.user.last_name || ""}`.trim()
          : "",
        "User Email": entry.user?.email || "",
        "Ticket Title": entry.ticket?.title || "",
        "Client Name": entry.ticket?.contacts?.name || "",
        "Company Name": entry.ticket?.contacts?.company_name || "",
        "Sync Status": entry.sync_status || "",
        "QuickBooks ID": entry.quickbooks_id || "",
        "Synced At": formatDateForCSV(entry.synced_at),
        "Created At": formatDateForCSV(entry.created_at),
        "Updated At": formatDateForCSV(entry.updated_at),
      }));

      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `time_entries_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error exporting time entries:", error);
    },
  });
}
